import React, { useState } from 'react';
import {
  Button,
  ModalContent,
  ModalContentCloseable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useUploadFile,
} from '@sandtech-hub-monorepo/ui-kit';
import { PDFFileUpload } from '@sandtech-hub-monorepo/ui-kit-site';
import { ReactComponent as EnglishProficiencyIcon } from '../../../assets/Icons/english-proficiency-icon.svg';
import {
  EnglishProficiencyType,
  UpdateProgramApplicationEnglishProficiencyProofDocument,
  UploadScope,
} from '@sandtech-hub-monorepo/ehub-client';
import { useMutation } from '@apollo/client';
import { Loader2Icon } from 'lucide-react';

const documentTypes = {
  'High-School Diploma': EnglishProficiencyType.HighSchoolDiploma,
  'University Transcript': EnglishProficiencyType.UniversityTranscript,
  'English Test Score': EnglishProficiencyType.EnglishTestScores,
  'English is my native language': EnglishProficiencyType.NativeLanguage,
};

interface Props extends ModalContentCloseable {
  applicationId: string;
}

export const EnglishProficiencyModal = ({ close, applicationId }: Props) => {
  const [selectedFile, setSelectedFile] = useState<File>();
  const { uploadFile, loading: uploadFileLoading } = useUploadFile();
  const [documentType, setDocumentType] = useState<EnglishProficiencyType>();
  const [mutate, { loading }] = useMutation(
    UpdateProgramApplicationEnglishProficiencyProofDocument
  );

  return (
    <ModalContent>
      <div className="flex flex-col items-center gap-2 max-w-screen-xs mx-auto">
        <div className="flex flex-col items-center text-center gap-2 mb-4">
          <div
            className={`p-6 bg-primary/20 rounded-full text-primary mx-auto mb-2`}
          >
            <EnglishProficiencyIcon height="48" width="48" />
          </div>
          <h2 className={`text-primary font-semibold sm:text-2xl text-xl`}>
            English proficiency
          </h2>
        </div>
        <div className="flex flex-col gap-6">
          <div>
            <div className="font-semibold">
              Elmwood Institute's School of Entrepreneurship and Technology is
              taught in English. Proof of English proficiency is required from
              students for whom English is not a native language, and can be
              demonstrated by one of the following documents:
            </div>
            <ul className="list-disc pl-6">
              <li>
                High-School transcript or diploma showing that the student
                graduated from a high school in which English was the medium of
                instruction.
              </li>
              <li>
                University transcript indicating at least the equivalent of 30
                earned credits with a C or higher in courses in which English
                was the medium of instruction.
              </li>
              <li>
                Standardised English proficiency test scores (Toefl, Duolingo,
                etc).
              </li>
              <li>
                Students for whom English is a native language may select this
                below, which will be verified based on their country of origin
                and/or residence.
              </li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-2">
              Please choose one type of English language proficiency you are
              providing:
            </div>
            <Select
              value={documentType || ''}
              onValueChange={(v) =>
                setDocumentType(v as EnglishProficiencyType)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Please select" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(documentTypes).map(([key, value]) => (
                  <SelectItem value={value} key={key}>
                    {key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {documentType !== EnglishProficiencyType.NativeLanguage && (
            <div>
              <div className="font-semibold mb-2">
                Please upload your proof of English language proficiency:
              </div>
              <PDFFileUpload value={selectedFile} onChange={setSelectedFile} />
            </div>
          )}
        </div>
        <Button
          onClick={async () => {
            let url: string | undefined;
            if (
              selectedFile &&
              documentType !== EnglishProficiencyType.NativeLanguage
            ) {
              url = await uploadFile(
                UploadScope.EnglishProficiencyDocument,
                selectedFile
              );
            }
            await mutate({
              variables: {
                input: {
                  id: applicationId,
                  type: documentType!,
                  url,
                },
              },
            });
            close();
          }}
          disabled={
            loading ||
            uploadFileLoading ||
            !documentType ||
            (!selectedFile &&
              documentType !== EnglishProficiencyType.NativeLanguage)
          }
          className="my-4"
        >
          {loading || uploadFileLoading ? (
            <>
              <Loader2Icon className="animate-spin" /> Submitting...
            </>
          ) : (
            'Submit'
          )}
        </Button>
      </div>
    </ModalContent>
  );
};
